package fr.frux.core.ads

/**
 * Emplacements admis par l'ADR 0030 (point 2). Une seule annonce visible à la fois : à claims
 * simultanés, la priorité la plus haute l'emporte (la bannière se masque devant la liste native ou
 * un plein écran), jamais l'ordre de composition.
 */
enum class AdPlacement(
    val priority: Int,
) {
    MAP_BANNER(1),
    NEARBY_NATIVE(2),
    INTERSTITIAL(3),
    REWARDED(3),
}

/** Raison de suspendre toute publicité. */
enum class AdHold {
    /** Onboarding en cours (ADR 0030 point 5) : ni annonce, ni appel à UMP ou à la régie. */
    ONBOARDING,

    /**
     * Ajout ou édition d'arbre, récolte, signalement, observation de terrain (ADR 0030 point 2).
     * Aucune annonce pendant le flux, ni au premier déclencheur qui le suit
     * (cf. [AdsController.maybeShowInterstitial]). Contrairement à [ONBOARDING], le SDK reste
     * initialisé et une annonce déjà préchargée est conservée : seul l'affichage est suspendu.
     */
    SENSITIVE_FLOW,
}

/** Pauses naturelles où un interstitiel peut être tenté (ADR 0030 point 2). Jamais au lancement. */
enum class InterstitialTrigger {
    /** Fermeture d'une fiche d'arbre, la première du process (appelant : la pile de navigation). */
    FIRST_TREE_DETAIL_CLOSED,

    /** Retour à la carte depuis un autre écran. */
    BACK_TO_MAP,
}

/** Avancement du consentement (UMP, TCF v2.2) pour la session courante. */
sealed interface AdConsent {
    /** UMP pas interrogé : première session, compte sans pub, plateforme sans pub, ou pas encore. */
    data object Unknown : AdConsent

    /** UMP exige un formulaire : afficher l'écran d'information « Publicité dans Frux ». */
    data object IntroPending : AdConsent

    data object FormShowing : AdConsent

    /** Accord, refus (annonces limitées, `canRequestAds` reste vrai) ou consentement non requis. */
    data class Resolved(
        val canRequestAds: Boolean,
    ) : AdConsent

    /** Échec, ou écran d'information écarté : tout reste fermé, nouvel essai à la session suivante. */
    data object Error : AdConsent
}

data class AdsState(
    /** `false` : web, desktop, iOS avant le jalon M4, ou publicité désactivée par la configuration. */
    val supported: Boolean,
    val consent: AdConsent,
    val eligible: Boolean,
    /** Seul emplacement autorisé à s'afficher, `null` si aucun ou si [eligible] est faux. */
    val visiblePlacement: AdPlacement?,
    /** Jeton de la claim gagnante : seule elle s'affiche, même si une autre vise le même emplacement. */
    val visibleClaimId: Long? = null,
    /** UMP exige un accès permanent aux choix publicitaires : entrée du Profil. */
    val privacyOptionsAvailable: Boolean,
    /** 1 = première session (aucune publicité), 0 tant que la session n'est pas établie. */
    val sessionIndex: Int,
) {
    companion object {
        /** État d'une plateforme ou d'une configuration sans publicité. */
        val UNSUPPORTED =
            AdsState(
                supported = false,
                consent = AdConsent.Unknown,
                eligible = false,
                visiblePlacement = null,
                privacyOptionsAvailable = false,
                sessionIndex = 0,
            )
    }
}

/** Jeton d'un hold ou d'un emplacement réclamé. [release] est idempotent. */
fun interface AdHandle {
    fun release()
}

/** Emplacement réclamé, identifié pour l'arbitrage (cf. [AdsState.visibleClaimId]). */
class AdClaim(
    val id: Long,
    private val onRelease: () -> Unit,
) : AdHandle {
    override fun release() = onRelease()
}
